import React from 'react'
import { useLocation } from 'react-router-dom'
import { Loader2, Check, ShieldCheck, Globe, Layers } from 'lucide-react'
import Logo from './Logo'
import AuthGauge from './AuthGauge'
import AuthConstellation from './AuthConstellation'
import './auth.css'

/**
 * AuthLayout — the ONE shared shell for every dedicated auth page
 * (/signin, /signup, /forgot-password, /reset-password). A tasteful split:
 *
 *   left  — a calm, dark brand panel with a SLOW ambient animation (two drifting
 *           auras + a few breathing constellation dots, all reduced-motion
 *           guarded), a short value proposition, and three trust points.
 *   right — the focused form (`children`).
 *
 * Every screen uses this layout, so colours, typography and the logo are
 * identical across all four — no more light-card / dark-card mismatch.
 */

// Sparse, hand-placed dots — kept few on purpose so the panel never feels busy.
const STARS = [
  { top: '14%', left: '20%', delay: '0s' },
  { top: '28%', left: '68%', delay: '1.4s' },
  { top: '44%', left: '12%', delay: '2.6s' },
  { top: '58%', left: '82%', delay: '0.8s' },
  { top: '72%', left: '36%', delay: '3.4s' },
  { top: '86%', left: '60%', delay: '2.0s' },
]

const TRUST = [
  { icon: ShieldCheck, text: 'Grounded only in your own sources — verified before it speaks.' },
  { icon: Layers, text: 'One login for every service. Pay only for what you switch on.' },
  { icon: Globe, text: 'Answers in 95+ languages, with the evidence attached.' },
]

export default function AuthLayout({ title, subtitle, children, gauge = false }) {
  const location = useLocation()
  // A/B the animated brand panel via ?anim= (default = the Verification Sweep radar).
  const variant = new URLSearchParams(location.search).get('anim') === 'constellation' ? 'constellation' : 'sweep'
  return (
    <div className="rrauth">
      {/* ── Brand panel ─────────────────────────────────────────────────── */}
      <aside className={`rrauth__brand ${gauge ? 'rrauth__brand--gauge' : ''}`} aria-hidden="false">
        <div className="rrauth__aura rrauth__aura--a" aria-hidden="true" />
        <div className="rrauth__aura rrauth__aura--b" aria-hidden="true" />
        <div className="rrauth__stars" aria-hidden="true">
          {STARS.map((s, i) => (
            <span key={i} className="rrauth__star" style={{ top: s.top, left: s.left, animationDelay: s.delay }} />
          ))}
        </div>

        {gauge ? (
          /* Animated brand panel IS the whole panel. A/B the concept via ?anim=. */
          variant === 'constellation' ? <AuthConstellation /> : <AuthGauge />
        ) : (
          <>
            <div className="rrauth__brand-top">
              <a href="/home" className="rrauth__brand-mark" aria-label="ReRight home">
                <Logo size="md" tone="light" />
              </a>
              <p className="rrauth__eyebrow">The AI platform, built on your data</p>
              <h2 className="rrauth__headline">
                AI that answers, sells, and <em>grows</em>.
              </h2>
              <p className="rrauth__lead">
                Bring your website, docs and systems once — then switch on a support chatbot, a
                voice receptionist, a private RAG API, or a marketing brain.
              </p>
            </div>

            <ul className="rrauth__trust">
              {TRUST.map(({ icon: Icon, text }, i) => (
                <li key={i}><Icon size={17} strokeWidth={2} /> <span>{text}</span></li>
              ))}
            </ul>
          </>
        )}
      </aside>

      {/* ── Form panel ──────────────────────────────────────────────────── */}
      <main className="rrauth__form">
        <div className="rrauth__card">
          <h1 className="rrauth__title">{title}</h1>
          {subtitle && <p className="rrauth__subtitle">{subtitle}</p>}
          <div className="rrauth__body">{children}</div>
        </div>
      </main>
    </div>
  )
}

// ── Shared form primitives (keep every page minimal + identical) ─────────────

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.5 0 6.6 1.2 9 3.6l6.7-6.7C35.6 2.4 30.1 0 24 0 14.6 0 6.5 5.4 2.6 13.2l7.8 6.1C12.3 13.2 17.7 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.5H24v9h12.4c-.5 2.9-2.1 5.3-4.6 7l7.1 5.5c4.1-3.8 6.7-9.5 6.7-16z" />
    <path fill="#FBBC05" d="M10.4 28.3a14.5 14.5 0 0 1 0-8.6l-7.8-6.1a24 24 0 0 0 0 20.8l7.8-6.1z" />
    <path fill="#34A853" d="M24 48c6.1 0 11.3-2 15-5.5l-7.1-5.5c-2 1.3-4.6 2.1-7.9 2.1-6.3 0-11.7-3.7-13.6-9l-7.8 6.1C6.5 42.6 14.6 48 24 48z" />
  </svg>
)

export function Field({ label, name, type = 'text', value, onChange, placeholder, autoComplete, required, minLength, autoFocus, disabled }) {
  return (
    <label className="rrauth__field">
      <span className="rrauth__label">{label}</span>
      <input
        className="rrauth__input"
        name={name}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete={autoComplete}
        required={required}
        minLength={minLength}
        autoFocus={autoFocus}
        disabled={disabled}
      />
    </label>
  )
}

export function SubmitButton({ busy, disabled, children, busyLabel = 'Working…' }) {
  return (
    <button type="submit" className="rrauth__btn rrauth__btn--primary" disabled={busy || disabled}>
      {busy ? (<><Loader2 size={17} className="rrauth__spin" /> {busyLabel}</>) : children}
    </button>
  )
}

export function GoogleButton({ onClick, label = 'Continue with Google', disabled }) {
  return (
    <button type="button" className="rrauth__btn rrauth__btn--google" onClick={onClick} disabled={disabled}>
      <GoogleIcon /> {label}
    </button>
  )
}

export function Divider({ label = 'or' }) {
  return <div className="rrauth__divider"><span>{label}</span></div>
}

export function Notice({ notice }) {
  if (!notice) return null
  const tone = notice.tone || 'info'
  return <div className={`rrauth__notice rrauth__notice--${tone}`}>{notice.text}</div>
}

export function AltLine({ children }) {
  return <p className="rrauth__alt">{children}</p>
}

export { Check }
